import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Edit2, Check, X } from 'lucide-react';

const SiteManagement = ({ user, activeModule = "b2b" }) => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newSiteName, setNewSiteName] = useState('');
  const [message, setMessage] = useState('');
  const [editingSite, setEditingSite] = useState(null);
  const [editName, setEditName] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      const response = await axios.get(`${API}/sites`, { withCredentials: true });
      setSites(response.data);
    } catch (error) {
      console.error('Error fetching sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSite = async (e) => {
    e.preventDefault();
    if (!newSiteName.trim()) return;

    try {
      await axios.post(`${API}/sites`, { name: newSiteName }, { withCredentials: true });
      setMessage(`✅ Site "${newSiteName}" créé!`);
      setNewSiteName('');
      fetchSites();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(`❌ ${error.response?.data?.detail || 'Erreur'}`);
    }
  };

  const handleEditSite = (site) => {
    setEditingSite(site.site_id);
    setEditName(site.name);
  };

  const handleSaveEdit = async (siteId) => {
    if (!editName.trim()) return;

    try {
      await axios.put(`${API}/sites/${siteId}`, { name: editName }, { withCredentials: true });
      setMessage(`✅ Site renommé en "${editName}"!`);
      setEditingSite(null);
      setEditName('');
      fetchSites();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(`❌ ${error.response?.data?.detail || 'Erreur lors de la modification'}`);
    }
  };

  const handleCancelEdit = () => {
    setEditingSite(null);
    setEditName('');
  };

  const handleDeleteSite = async (siteId, siteName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le site "${siteName}" ?`)) return;
    
    try {
      await axios.delete(`${API}/sites/${siteId}`, { withCredentials: true });
      setMessage(`✅ Site supprimé!`);
      fetchSites();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(`❌ ${error.response?.data?.detail || 'Erreur'}`);
    }
  };

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="p-6 max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/admin/planning')}
          className="flex items-center gap-2 mb-4 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour au planning
        </button>

        <h1 className="text-2xl font-bold mb-6">Gestion des Sites/Sociétés</h1>

        {message && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-center">
            {message}
          </div>
        )}

        {/* Ajouter un site */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <h2 className="font-bold mb-4">Ajouter un nouveau site</h2>
            <form onSubmit={handleAddSite} className="flex gap-2">
              <input
                type="text"
                value={newSiteName}
                onChange={(e) => setNewSiteName(e.target.value)}
                placeholder="Nom du site (ex: Amazon)"
                className="flex-1 px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                required
              />
              <button
                type="submit"
                className="px-6 py-3 text-lg bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700"
              >
                AJOUTER
              </button>
            </form>
          </CardContent>
        </Card>

        {/* Liste des sites */}
        <Card>
          <CardContent className="p-6">
            <h2 className="font-bold mb-4">Sites existants ({sites.length})</h2>
            {loading ? (
              <p>Chargement...</p>
            ) : sites.length === 0 ? (
              <p className="text-gray-400 italic">Aucun site créé</p>
            ) : (
              <div className="space-y-3">
                {sites.map(site => (
                  <div
                    key={site.site_id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border-2 border-gray-200 hover:border-gray-300 transition-colors"
                  >
                    {editingSite === site.site_id ? (
                      /* Mode édition */
                      <div className="flex items-center gap-2 flex-1">
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="flex-1 px-3 py-2 text-lg border-2 border-blue-500 rounded-lg focus:outline-none"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEdit(site.site_id);
                            if (e.key === 'Escape') handleCancelEdit();
                          }}
                        />
                        <button
                          onClick={() => handleSaveEdit(site.site_id)}
                          className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                          title="Enregistrer"
                        >
                          <Check className="w-5 h-5" />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="p-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                          title="Annuler"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ) : (
                      /* Mode affichage */
                      <>
                        <span className="text-lg font-medium">{site.name}</span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditSite(site)}
                            className="px-4 py-3 bg-amber-500 text-white rounded-lg font-bold hover:bg-amber-600 text-lg transition-colors flex items-center gap-2"
                            type="button"
                          >
                            <Edit2 className="w-5 h-5" />
                            MODIFIER
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteSite(site.site_id, site.name);
                            }}
                            className="px-4 py-3 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 text-lg active:bg-red-800 transition-colors cursor-pointer"
                            type="button"
                          >
                            SUPPRIMER
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default SiteManagement;
